// src/pages/orders/orderHistoryScreen/OrderHistoryScreen.tsx
import React, { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MdHistory } from "react-icons/md";
import { useTheme } from "../../../common/hooks/useTheme";
import { useOrderHistory } from "./hooks/useOrderHistory";
import { useRepeatOrder } from "../useRepeatOrder";
import { MobileHeader } from "../../../common/components/mobileHeader/MobileHeader";
import { ProductHeader } from "../../../common/components/productHeader/ProductHeader";
import OrderHistoryItem from "./components/orderHistoryItem/OrderHistoryItem";
import { EmptyState } from "../../../common/components/emptyState/EmptyState";
import styles from "./OrderHistoryScreen.module.scss";
import { ROUTES } from "../../../common/utils/constants";
import { PageMeta } from "../../../common/components/pageMeta/PageMeta";
import { useSelector } from "react-redux";
import {
  selectClientUserId,
  selectIsAdmin,
  selectIsExecutor,
} from "../../../store/slices/executorSlice";

export default function OrderHistoryScreen() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { orders, activeTab, setActiveTab } = useOrderHistory();
  const { repeatOrder, isRepeatDisabled } = useRepeatOrder();
  const isExecutor = useSelector(selectIsExecutor);
  const isAdmin = useSelector(selectIsAdmin);
  const clientUserId = useSelector(selectClientUserId);
  const showRepeat = !!clientUserId || (!isExecutor && !isAdmin);
  const tabsRef = useRef<HTMLDivElement>(null);

  const handlePress = (orderId: number) => {
    navigate(`${ROUTES.ORDER_DETAILS}/${orderId}`);
  };

  const subtitle = orders.length > 0 ? `${orders.length} зак.` : "Ваши заказы";

  useEffect(() => {
    if (!activeTab || !tabsRef.current) return;

    const activeButton = tabsRef.current.querySelector(
      `.${styles.activeTab}`
    ) as HTMLElement | null;

    if (activeButton) {
      activeButton.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeTab]);

  return (
    <div
      className={styles.container}
      style={{ backgroundColor: theme.background }}
    >
      <PageMeta />

      <div className={styles.mobileOnly}>
        <MobileHeader title="История заказов" subtitle={subtitle} />
      </div>

      <div className={styles.desktopOnly}>
        <ProductHeader
          title="История заказов"
          isFavorite={false}
          showFavorite={false}
          textColor={theme.text}
          borderColor={theme.border}
        />
      </div>

      <div className={styles.content}>
        <div className={styles.tabsWrapper}>
          <div ref={tabsRef} className={styles.tabs}>
            <button
              className={`${styles.tab} ${activeTab === "all" ? styles.activeTab : ""}`}
              onClick={() => setActiveTab("all")}
            >
              Все
            </button>
            <button
              className={`${styles.tab} ${activeTab === "active" ? styles.activeTab : ""}`}
              onClick={() => setActiveTab("active")}
            >
              Активные
            </button>
            <button
              className={`${styles.tab} ${activeTab === "completed" ? styles.activeTab : ""}`}
              onClick={() => setActiveTab("completed")}
            >
              Завершённые
            </button>
          </div>
        </div>

        {orders.length === 0 ? (
          <EmptyState
            icon={MdHistory}
            title="История заказов пуста"
            description="Здесь появятся ваши заказы после первого оформления"
          />
        ) : (
          <div className={styles.ordersList}>
            {orders.map((order) => (
              <OrderHistoryItem
                key={order.id}
                order={order}
                onPress={handlePress}
                onRepeat={showRepeat ? repeatOrder : undefined}
                isRepeatDisabled={showRepeat ? isRepeatDisabled : undefined}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}